import React from 'react'
import { Tooltip } from '@paintscout/ui'
import type { TooltipProps } from '@paintscout/ui'
import type { Payment } from 'paintscout'
import PaymentStatusBadge from './PaymentStatusBadge'

export interface PaymentStatusBadgeWithTooltipProps {
  status: Payment['status']
  amount: number
  type: string
  balanceDue: number
  achLabel: string
  failedEvents?: string[]
  inPresentation?: boolean
  /**
   * Where the tooltip opens relative to the badge. Defaults to MUI's `bottom`. Set this to open
   * toward the page interior when the badge sits near a viewport edge (e.g. `left` for a
   * right-aligned badge), so the tooltip isn't clipped by the edge of the page.
   */
  placement?: TooltipProps['placement']
}

/**
 * A PaymentStatusBadge wrapped with a contextual tooltip explaining the payment's status:
 * - pending requests that exceed the balance due
 * - how long processing payments (ACH/EFT vs. card) take to resolve
 * - the events that caused a failed payment
 *
 * Falls back to a bare badge when there's nothing useful to explain.
 */
export default function PaymentStatusBadgeWithTooltip({
  status,
  amount,
  type,
  balanceDue,
  achLabel,
  failedEvents,
  inPresentation,
  placement
}: PaymentStatusBadgeWithTooltipProps) {
  const failedTooltip =
    !inPresentation && status === 'failed'
      ? (failedEvents ?? []).filter((e) => e !== 'fully paid' && e !== 'Code 200 Error').join(', ') || null
      : null

  const tooltipContent =
    status === 'pending' && amount > balanceDue
      ? 'Requested amount exceeds balance due. Your customer will not be able to see this request.'
      : status === 'processing'
        ? ['ach', 'eft'].includes(type?.toLowerCase())
          ? `${achLabel} payments usually take up to 5 business days to resolve.`
          : 'This payment should resolve within a few hours.'
        : failedTooltip

  if (tooltipContent) {
    return (
      <Tooltip hideIcon content={tooltipContent} placement={placement}>
        <PaymentStatusBadge status={status} />
      </Tooltip>
    )
  }
  return <PaymentStatusBadge status={status} />
}
